import React from 'react';
import { useTheme } from '../context/ThemeContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { 
  Stethoscope, 
  Heart, 
  Users, 
  Mail, 
  MapPin, 
  Sparkles,
  CheckCircle,
  ArrowRight
} from 'lucide-react';

const CareerModal = ({ isOpen, onClose }) => {
  const { isDark } = useTheme();
  const positions = [
    {
      icon: Stethoscope,
      title: 'Ärzte (m/w/d)',
      description: 'Fachärzte für Dermatologie oder Plastische Chirurgie mit Interesse an Haartransplantation',
    },
    {
      icon: Heart,
      title: 'MFA (m/w/d)',
      description: 'Medizinische Fachangestellte zur Unterstützung bei Behandlungen und Patientenbetreuung',
    },
    {
      icon: Users,
      title: 'Patientenberater (m/w/d)',
      description: 'Empathische Berater für die Erstgespräche und Betreuung unserer Patienten',
    },
  ];

  const benefits = [
    'Modernste Arbeitsumgebung',
    'Attraktive Vergütung',
    'Weiterbildungsmöglichkeiten',
    'Familiäres Team',
    'Flexible Arbeitszeiten',
    'Zentrale Lage in NRW',
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`max-w-2xl max-h-[90vh] overflow-y-auto p-0 transition-colors duration-300 ${
        isDark ? 'bg-[#0f0f0f] border-white/10' : 'bg-white border-gray-200'
      }`}>
        {/* Header with gradient */}
        <div className="relative overflow-hidden">
          <div className={`absolute inset-0 ${
            isDark 
              ? 'bg-gradient-to-br from-[#7BA7C2]/20 via-transparent to-[#5A8BA6]/10'
              : 'bg-gradient-to-br from-[#5A8BA6]/10 via-transparent to-[#7BA7C2]/5'
          }`} />
          <div className="relative p-8 pb-6">
            <DialogHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#7BA7C2] to-[#5A8BA6] flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <DialogTitle className={`text-2xl ${isDark ? 'text-white' : 'text-gray-900'}`}>Karriere bei Dion Hair Clinic</DialogTitle>
                  <DialogDescription className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                    Werden Sie Teil unseres Teams
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>
          </div>
        </div>

        {/* Content */}
        <div className="px-8 pb-8 space-y-8">
          {/* Intro Text */}
          <div className={`leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            <p>
              Wir sind eine moderne Haarklinik in Mönchengladbach und suchen engagierte 
              Talente, die unsere Leidenschaft für ästhetische Medizin teilen. Bei uns 
              erwartet Sie ein dynamisches Arbeitsumfeld mit neuesten Technologien und 
              einem herzlichen Team.
            </p>
          </div>

          {/* Positions */}
          <div>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Wir suchen Verstärkung</h3>
            <div className="space-y-3">
              {positions.map((position, index) => (
                <div 
                  key={index}
                  className={`flex items-start gap-4 p-4 rounded-xl border transition-colors ${
                    isDark 
                      ? 'bg-white/[0.03] border-white/5 hover:border-[#7BA7C2]/30'
                      : 'bg-gray-50 border-gray-200 hover:border-[#5A8BA6]/30'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    isDark ? 'bg-[#7BA7C2]/10' : 'bg-[#5A8BA6]/10'
                  }`}>
                    <position.icon className={`w-5 h-5 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
                  </div>
                  <div>
                    <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{position.title}</h4>
                    <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{position.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Benefits */}
          <div>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Was wir bieten</h3>
            <div className="grid grid-cols-2 gap-3">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
                  <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className={`rounded-xl p-6 border ${
            isDark 
              ? 'bg-gradient-to-br from-[#7BA7C2]/10 to-[#5A8BA6]/5 border-[#7BA7C2]/20'
              : 'bg-gradient-to-br from-[#5A8BA6]/5 to-[#7BA7C2]/5 border-[#5A8BA6]/20'
          }`}>
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                isDark ? 'bg-[#7BA7C2]/20' : 'bg-[#5A8BA6]/20'
              }`}>
                <Mail className={`w-6 h-6 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
              </div>
              <div className="flex-1">
                <h4 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Interesse geweckt?</h4>
                <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Senden Sie uns Ihre Bewerbungsunterlagen – wir freuen uns darauf, Sie kennenzulernen!
                </p>
                <a 
                  href="mailto:info@dionhairclinic.de?subject=Bewerbung bei Dion Hair Clinic"
                  className={`inline-flex items-center gap-2 font-medium transition-colors ${
                    isDark ? 'text-[#7BA7C2] hover:text-white' : 'text-[#5A8BA6] hover:text-gray-900'
                  }`}
                >
                  info@dionhairclinic.de
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>

          {/* Location Info */}
          <div className={`flex items-center gap-3 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            <MapPin className={`w-4 h-4 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
            <span>Schürenweg 61, 41063 Mönchengladbach – Ideal erreichbar aus Köln, Düsseldorf und dem Ruhrgebiet</span>
          </div>

          {/* Close Button */}
          <div className="flex justify-end pt-2">
            <Button
              onClick={onClose}
              className="bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] hover:from-[#5A8BA6] hover:to-[#7BA7C2] text-white"
            >
              Schließen
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CareerModal;
